import { Component, Inject, OnInit } from '@angular/core';
import { NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  delay,
  filter,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
  throwError,
} from 'rxjs';
import { OrderType } from 'src/app/api/contracts/order';
import { Product } from 'src/app/api/contracts/product';
import { ScannedProductToastComponent } from 'src/app/components/UI/scanned-product-toast/scanned-product-toast.component';
import { CartService } from 'src/app/services/cart.service';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { InStoreProductsService } from 'src/app/services/in-store-products.service';
import { LocationService } from 'src/app/services/location.service';
import { ModalService } from 'src/app/services/modal.service';
import { PlatformService } from 'src/app/services/platform.service';
import {
  APP_SCANNER,
  AbstractScanner,
} from 'src/app/services/scanner/scanner.types';
import { GenericModalType } from 'src/app/types/modals';
import { LoaderController } from 'src/app/utils/loader-controller';
import {
  hashValidator,
  supportedBarcodesValidators,
} from 'src/app/validators/common';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-product-scanner',
  templateUrl: './product-scanner.component.html',
  styleUrls: ['./product-scanner.component.scss'],
})
export class ProductScannerComponent implements OnInit {
  public readonly loader = new LoaderController();
  public readonly pageStates = PAGE_STATES;
  public pageState: PAGE_STATES = PAGE_STATES.LOCATION_AUTH;
  public scanErrorType: SCAN_ERRORS | null = null;

  public readonly isDebug = !environment.production;
  public debugMessage: string | null = null;
  public pageIsOutOfFocus: boolean = false;
  private readonly toastDuration = 500000;

  private readonly manualBarcodeInput$ = new Subject<string>();

  constructor(
    @Inject(APP_SCANNER) public readonly scannerService: AbstractScanner,
    public readonly platformService: PlatformService,
    private readonly inStoreProductsService: InStoreProductsService,
    private readonly navCtrl: NavController,
    private readonly modalService: ModalService,
    private readonly translateService: TranslateService,
    private readonly locationService: LocationService,
    private readonly customToastService: CustomToastService,
    private readonly cartService: CartService
  ) {
    const isInSession =
      !this.inStoreProductsService.hasCurrentInstoreSessionExpired();
    if (isInSession) {
      this.pageState = this.pageStates.PRODUCT_SCANNING;
    }
  }

  ngOnInit() {
    this.subscribeForValueScanning();
  }

  ionViewWillEnter() {
    this.pageIsOutOfFocus = false;
  }

  ionViewWillLeave() {
    this.pageIsOutOfFocus = true;
  }

  onManualBarcodeInputClick(): void {
    this.pageIsOutOfFocus = true;
    this.modalService.showManualBarcodeInputModal().subscribe((barcode) => {
      this.pageIsOutOfFocus = false;
      if (!barcode) {
        return;
      }
      this.manualBarcodeInput$.next(barcode);
    });
  }

  onProductSearchClick(): void {
    const locationHash =
      this.inStoreProductsService.currentInstoreSession$.getValue();
    this.pageIsOutOfFocus = true;
    this.modalService
      .showProductSearchModal(locationHash?.location.hash!)
      .subscribe((product) => {
        this.pageIsOutOfFocus = false;
        if (!product) {
          return;
        }
        this.addProductToCart(product).subscribe();
      });
  }

  private subscribeForValueScanning(): void {
    merge(
      this.scannerService.valueScanned,
      this.manualBarcodeInput$.asObservable()
    )
      .pipe(
        throttleTime(2000),
        filter(() => !this.loader.isLoadingNow() && !this.pageIsOutOfFocus),
        tap((value) => {
          this.debugMessage = `DEBUG MESSAGE - scanned value: ${value}`;
        })
      )
      .subscribe((scannedValue) => {
        if (this.loader.isLoadingNow()) {
          return;
        }
        if (this.pageState === this.pageStates.LOCATION_AUTH) {
          this.handleStoreQrCodeScanning(scannedValue);
          return;
        }
        if (this.pageState === this.pageStates.PRODUCT_SCANNING) {
          const isSessionExpired =
            this.inStoreProductsService.hasCurrentInstoreSessionExpired();
          if (!isSessionExpired) {
            return this.handleProductBarcodeCodeScanning(scannedValue);
          }
          this.loader.enqueue();
          return this.modalService
            .showGenericInfoPopup({
              header: this.translateService.instant(
                '@pages.scanner.sessionExpired.header'
              ),
              message: this.translateService.instant(
                '@pages.scanner.sessionExpired.message'
              ),
              type: GenericModalType.WARNING,
            })
            .subscribe(() => {
              this.loader.dequeue();
              this.pageState = this.pageStates.LOCATION_AUTH;
            });
        }
      });
  }

  private handleStoreQrCodeScanning(storeHash: string = ''): void {
    const isValueValid: boolean = !!hashValidator.exec(storeHash)?.length;
    if (!isValueValid) {
      this.triggerErrorState(
        PAGE_STATES.LOCATION_AUTH_ERROR,
        SCAN_ERRORS.LOCATION_QR_INVALID
      );
      return;
    }
    this.loader.enqueue();
    this.locationService
      .retrieveLocationByHash(storeHash)
      .pipe(
        catchError((err) => {
          this.loader.dequeue();
          this.triggerErrorState(
            PAGE_STATES.LOCATION_AUTH_ERROR,
            SCAN_ERRORS.LOCATION_NOT_FOUND
          );
          return throwError(() => new Error(err));
        })
      )
      .subscribe((location) => {
        this.loader.dequeue();
        this.inStoreProductsService.startInstoreSession(location);
        this.pageState = this.pageStates.PRODUCT_SCANNING;
      });
  }

  private handleProductBarcodeCodeScanning(scannedBarcode: string = ''): void {
    let barcode = `${scannedBarcode}`;
    const isValueValid: boolean = supportedBarcodesValidators.some(
      (validator) => validator.exec(barcode)?.length
    );
    if (!isValueValid) {
      this.triggerErrorState(
        PAGE_STATES.PRODUCT_SCANNING_ERROR,
        SCAN_ERRORS.BARCODE_INVALID
      );
      return;
    }
    this.loader.enqueue();
    const locationHash =
      this.inStoreProductsService.currentInstoreSession$.getValue()!.location
        .hash;
    this.inStoreProductsService
      .findInStoreProductByBarcode(barcode, locationHash)
      .pipe(
        catchError((err) => {
          this.loader.dequeue();
          this.triggerErrorState(
            PAGE_STATES.PRODUCT_SCANNING_ERROR,
            SCAN_ERRORS.PRODUCT_NOT_FOUND
          );
          return throwError(() => new Error(err));
        }),
        switchMap((product) => this.addProductToCart(product))
      )
      .subscribe();
  }
  /**
   *
   * @param product
   * @returns true if was added, false if was cancelled
   */
  private addProductToCart(product: Product): Observable<boolean> {
    this.loader.dequeue();
    this.cartService.addItemToCart(
      product,
      OrderType.IN_STORE,
      null,
      null,
      null
    );
    return this.customToastService
      .showToast<ScannedProductToastComponent, boolean>({
        component: ScannedProductToastComponent,
        componentInputs: {
          product,
        },
        duration: this.toastDuration,
      })
      .pipe(
        switchMap((shouldUndo) => {
          if (!shouldUndo) {
            return of(true);
          }
          return this.cartService.currentCart$.pipe(
            map((cart) =>
              cart.items.find((item) => item.product.id === product.id)
            ),
            take(1),
            tap((cartItem) => {
              this.cartService.decreaseAmount(cartItem!);
            }),
            map(() => false)
          );
        })
      );
  }

  private triggerErrorState(
    pageState: PAGE_STATES,
    errorType: SCAN_ERRORS
  ): void {
    this.scanErrorType = errorType;
    const messageDuration = 3000;
    const prevState = this.pageState;
    this.pageState = pageState;
    of(void 0)
      .pipe(takeUntil(this.scannerService.valueScanned), delay(messageDuration))
      .subscribe(() => {
        this.pageState = prevState;
      });
  }
}

enum PAGE_STATES {
  LOCATION_AUTH = 'LOCATION_AUTH',
  PRODUCT_SCANNING = 'PRODUCT_SCANNING',
  LOCATION_AUTH_ERROR = 'LOCATION_AUTH_ERROR',
  PRODUCT_SCANNING_ERROR = 'PRODUCT_SCANNING_ERROR',
}

enum SCAN_ERRORS {
  LOCATION_QR_INVALID = 'LOCATION_QR_INVALID',
  LOCATION_NOT_FOUND = 'LOCATION_NOT_FOUND',
  BARCODE_INVALID = 'BARCODE_INVALID',
  PRODUCT_NOT_FOUND = 'PRODUCT_NOT_FOUND',
}
